/**
 * Autore: Salvo de Luca
 * Azienda Dialogicom s.r.l.s.
 * File: src/components/modular-form/custom/RichTextEditor.tsx
 * Percorso: frontend3/src/components/modular-form/custom/RichTextEditor.tsx
 * Release: v1.3.0 - Editor Rich Text con preview real-time side-by-side
 * Data: 14/07/2025
 */

import React, { useState, useEffect, useRef } from 'react';
import { 
  Bold, 
  Italic, 
  Underline, 
  List, 
  ListOrdered, 
  Link, 
  AlertCircle
} from 'lucide-react';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  maxLength?: number;
  required?: boolean;
  error?: string;
  disabled?: boolean;
  className?: string;
}

// Funzione per convertire Markdown in HTML
const markdownToHtml = (markdown: string): string => {
  return markdown
    // Grassetto
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/__(.*?)__/g, '<strong>$1</strong>')
    // Corsivo
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/_(.*?)_/g, '<em>$1</em>')
    // Sottolineato
    .replace(/~~(.*?)~~/g, '<u>$1</u>')
    // Titoli
    .replace(/^### (.*$)/gim, '<h3>$1</h3>')
    .replace(/^## (.*$)/gim, '<h2>$1</h2>')
    .replace(/^# (.*$)/gim, '<h1>$1</h1>')
    // Liste
    .replace(/^\d+\. (.*$)/gim, '<li>$1</li>')
    .replace(/^- (.*$)/gim, '<li>$1</li>')
    // Collegamenti
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-teal-500 underline">$1</a>')
    // Paragrafi
    .replace(/\n\n/g, '</p><p>')
    .replace(/^(?!<[h|li|p])(.*$)/gim, '<p>$1</p>')
    // Pulizia
    .replace(/<p><\/p>/g, '')
    .replace(/<p><p>/g, '<p>')
    .replace(/<\/p><\/p>/g, '</p>');
};

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value = '',
  onChange,
  label,
  placeholder = 'Inserisci il testo...',
  maxLength = 500,
  required = false,
  error,
  disabled = false,
  className = ''
}) => {
  const [charCount, setCharCount] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Aggiorna il conteggio caratteri quando cambia il valore
  useEffect(() => {
    setCharCount(value.length);
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    
    // Limita la lunghezza se supera il massimo
    if (newValue.length <= maxLength) {
      onChange(newValue);
    }
  };

  const formatText = (format: string) => {
    if (!textareaRef.current || disabled) return;
    
    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);
    
    let formattedText = '';
    switch (format) {
      case 'bold':
        formattedText = `**${selectedText}**`;
        break;
      case 'italic':
        formattedText = `*${selectedText}*`;
        break;
      case 'underline':
        formattedText = `~~${selectedText}~~`;
        break;
      case 'list':
        formattedText = `- ${selectedText}`;
        break;
      case 'list-ordered':
        formattedText = `1. ${selectedText}`;
        break;
      case 'link':
        formattedText = `[${selectedText}](url)`;
        break;
      default:
        return;
    }
    
    const newValue = value.substring(0, start) + formattedText + value.substring(end);
    
    if (newValue.length <= maxLength) {
      onChange(newValue);
      
      // Ripristina il focus e la selezione
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start, start + formattedText.length);
      }, 0);
    }
  };

  const isOverLimit = charCount > maxLength;
  const remainingChars = maxLength - charCount;

  return (
    <div className={`w-full ${className}`}>
      {/* Label */}
      {label && (
        <label className="block text-sm font-medium text-zinc-300 mb-2">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Editor Container */}
      <div className="relative">
        <div
          className={`
            border rounded-lg overflow-hidden transition-all duration-200
            ${error 
              ? 'border-red-500' 
              : 'border-zinc-600 focus-within:border-teal-500'
            }
            ${disabled ? 'opacity-60' : ''}
          `}
        >
          {/* Toolbar */}
          <div className="flex items-center justify-between p-2 bg-zinc-800 border-b border-zinc-600">
            <div className="flex items-center space-x-1">
              <button
                type="button"
                onClick={() => formatText('bold')}
                className="p-1 rounded text-zinc-300 hover:text-white hover:bg-zinc-600 transition-colors"
                disabled={disabled}
                title="Grassetto"
              >
                <Bold size={16} />
              </button>
              <button
                type="button"
                onClick={() => formatText('italic')}
                className="p-1 rounded text-zinc-300 hover:text-white hover:bg-zinc-600 transition-colors"
                disabled={disabled}
                title="Corsivo"
              >
                <Italic size={16} />
              </button>
              <button
                type="button"
                onClick={() => formatText('underline')}
                className="p-1 rounded text-zinc-300 hover:text-white hover:bg-zinc-600 transition-colors"
                disabled={disabled}
                title="Sottolineato"
              >
                <Underline size={16} />
              </button>
              
              <div className="w-px h-6 bg-zinc-600 mx-1" />
              
              <button
                type="button"
                onClick={() => formatText('list')}
                className="p-1 rounded text-zinc-300 hover:text-white hover:bg-zinc-600 transition-colors"
                disabled={disabled}
                title="Lista puntata"
              >
                <List size={16} />
              </button>
              <button
                type="button"
                onClick={() => formatText('list-ordered')}
                className="p-1 rounded text-zinc-300 hover:text-white hover:bg-zinc-600 transition-colors"
                disabled={disabled}
                title="Lista numerata"
              >
                <ListOrdered size={16} />
              </button>
              
              <div className="w-px h-6 bg-zinc-600 mx-1" />
              
              <button
                type="button"
                onClick={() => formatText('link')}
                className="p-1 rounded text-zinc-300 hover:text-white hover:bg-zinc-600 transition-colors"
                disabled={disabled}
                title="Collegamento"
              >
                <Link size={16} />
              </button>
            </div>
            
            <div className="text-xs text-zinc-400 font-medium">
              Markdown
            </div>
          </div>

          {/* Editor e Preview side-by-side */}
          <div className="grid grid-cols-2 gap-0">
            {/* Editor */}
            <div className="border-r border-zinc-600">
              <textarea
                ref={textareaRef}
                value={value}
                onChange={handleChange}
                placeholder={placeholder}
                disabled={disabled}
                maxLength={maxLength}
                className="w-full min-h-[120px] p-3 bg-zinc-800 text-zinc-300 placeholder-zinc-500 resize-none focus:outline-none focus:ring-0 border-0"
                style={{ fontFamily: 'Monaco, Menlo, Ubuntu Mono, monospace' }}
              />
            </div>
            
            {/* Preview */}
            <div className="min-h-[120px] p-3 bg-zinc-800 text-zinc-300 overflow-y-auto">
              {value ? (
                <div 
                  className="prose prose-invert max-w-none"
                  dangerouslySetInnerHTML={{ 
                    __html: markdownToHtml(value) 
                  }}
                />
              ) : (
                <p className="text-zinc-500 italic text-sm">Anteprima...</p>
              )}
            </div>
          </div>
        </div>

        {/* Contatore caratteri */}
        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center space-x-2">
            {/* Error message */}
            {error && (
              <div className="flex items-center text-red-400 text-sm">
                <AlertCircle size={14} className="mr-1" />
                {error}
              </div>
            )}
          </div>
          
          {/* Character count */}
          <div className={`
            text-xs font-mono
            ${isOverLimit ? 'text-red-400' : 'text-zinc-400'}
          `}>
            {charCount}/{maxLength}
            {isOverLimit && (
              <span className="ml-1 text-red-400">
                ({remainingChars} caratteri in eccesso)
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Help text */}
      <div className="mt-2 text-xs text-zinc-400">
        <p>Formattazione: seleziona il testo e usa i pulsanti della toolbar. Supporta: <code>**grassetto**</code>, <code>*corsivo*</code>, <code>~~sottolineato~~</code>, liste, collegamenti</p>
      </div>
    </div>
  );
};

export default RichTextEditor; 